// apps/prompts/src/components/ErrorBoundary.tsx
import { AppIcon, IconAlertTriangle } from '@neuroforge/ui'
import { Component, type ReactNode, type ErrorInfo } from 'react'

interface ErrorBoundaryProps {
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode)
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const { fallback } = this.props
      if (typeof fallback === 'function') {
        return fallback(this.state.error, this.handleReset)
      }
      if (fallback) return fallback
      return <DefaultErrorFallback error={this.state.error} onReset={this.handleReset} />
    }
    return this.props.children
  }
}

// ─── Default fallback ───
function DefaultErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
      <AppIcon
        icon={IconAlertTriangle}
        size={28}
        className="mb-4 h-12 w-12 text-red-500"
        aria-hidden
      />
      <h2 className="mb-2 text-xl font-extrabold tracking-tight">Une erreur est survenue</h2>
      <p className="mb-6 max-w-md text-sm text-slate-500">
        {error.message || 'Erreur inattendue. Essaie de recharger la page.'}
      </p>
      <div className="flex gap-3">
        <button
          onClick={onReset}
          className="rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-600"
        >
          Réessayer
        </button>
        <button
          onClick={() => (window.location.href = '/')}
          className="rounded-xl bg-slate-200 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600"
        >
          Retour à l'accueil
        </button>
      </div>
      {import.meta.env.DEV && (
        <details className="mt-6 w-full max-w-lg text-left">
          <summary className="cursor-pointer text-xs text-slate-400">Stack trace (dev)</summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-100 p-4 text-[10px] text-slate-500 dark:bg-slate-800">
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  )
}
